import React, { useEffect, useMemo, useState } from "react";
import { Check, Eye, X } from "lucide-react";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import {
  getSuperAdminOfflinePlanRequests,
  PaginatedResponse,
  rejectSuperAdminOfflinePlanRequest,
  SuperAdminOfflinePlanRequest,
  verifySuperAdminOfflinePlanRequest,
} from "../../services/superAdminApi";

const cardCls = "bg-white rounded-xl shadow-sm border border-gray-200 p-4";

const statusBadge = (status?: string) => {
  const normalized = (status || "").toLowerCase();
  const cls =
    normalized === "verified"
      ? "bg-green-100 text-green-700"
      : normalized === "rejected"
        ? "bg-red-100 text-red-700"
        : "bg-yellow-100 text-yellow-700";
  return <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${cls}`}>{normalized || "pending"}</span>;
};

const SuperAdminOfflineRequests: React.FC = () => {
  const [rows, setRows] = useState<SuperAdminOfflinePlanRequest[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<SuperAdminOfflinePlanRequest> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [perPage, setPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [actionType, setActionType] = useState<"verify" | "reject" | null>(null);
  const [selected, setSelected] = useState<SuperAdminOfflinePlanRequest | null>(null);

  const fetchRows = async (targetPage = page, searchOverride?: string, statusOverride?: string) => {
    const effectiveSearch = searchOverride ?? search;
    const effectiveStatus = statusOverride ?? status;
    try {
      setLoading(true);
      setError("");
      const res = await getSuperAdminOfflinePlanRequests({
        per_page: perPage,
        page: targetPage,
        search: effectiveSearch || undefined,
        status: effectiveStatus || undefined,
      });
      setRows(res.data || []);
      setPagination(res);
      setPage(res.current_page || targetPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load offline requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  const total = pagination?.total ?? rows.length;
  const currentPage = pagination?.current_page ?? 1;
  const lastPage = pagination?.last_page ?? 1;

  const counts = useMemo(() => {
    return rows.reduce(
      (acc, item) => {
        const normalized = (item.status || "").toLowerCase();
        if (normalized === "verified") acc.verified += 1;
        else if (normalized === "rejected") acc.rejected += 1;
        else acc.pending += 1;
        return acc;
      },
      { pending: 0, verified: 0, rejected: 0 }
    );
  }, [rows]);

  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    try {
      if (actionType === "verify") {
        await verifySuperAdminOfflinePlanRequest(selected.id);
      } else {
        await rejectSuperAdminOfflinePlanRequest(selected.id);
      }
      setActionType(null);
      setSelected(null);
      await fetchRows(currentPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Action failed for offline request.");
    }
  };

  return (
    <div className="space-y-5 p-1">
      <div>
        <h1 className="text-2xl font-semibold text-[#402B69]">Offline Request</h1>
        <p className="text-sm text-gray-500">Review offline plan change requests from businesses.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Total Requests</p>
          <p className="text-2xl font-bold text-[#402B69]">{total}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Pending (Page)</p>
          <p className="text-2xl font-bold text-yellow-600">{counts.pending}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Verified (Page)</p>
          <p className="text-2xl font-bold text-green-600">{counts.verified}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Rejected (Page)</p>
          <p className="text-2xl font-bold text-red-600">{counts.rejected}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[240px]">
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Company / Package / Payment by..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rows per page</label>
          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <button
          onClick={() => fetchRows(1)}
          className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Apply
        </button>
        <button
          onClick={() => {
            setSearch("");
            setStatus("");
            fetchRows(1, "", "");
          }}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Reset
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-600">Loading offline requests...</div>
        ) : error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">ID</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Company</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Package</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Payment By</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-gray-500" colSpan={6}>
                        No offline requests found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const isPending = (row.status || "").toLowerCase() === "pending";
                      return (
                        <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                          <td className="px-4 py-3 text-gray-700">{row.id}</td>
                          <td className="px-4 py-3 text-gray-700">{row.company?.company_name || "-"}</td>
                          <td className="px-4 py-3 text-gray-700">{row.package?.name || "-"}</td>
                          <td className="px-4 py-3 text-gray-700">{row.offline_method?.name || "-"}</td>
                          <td className="px-4 py-3 text-gray-700">{statusBadge(row.status)}</td>
                          <td className="px-4 py-3 text-gray-700">
                            <div className="flex justify-end gap-2">
                              <a
                                href={row.file || "#"}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-100 text-cyan-700"
                                title="View File"
                              >
                                <Eye size={15} />
                              </a>
                              {isPending ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelected(row);
                                      setActionType("verify");
                                    }}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-green-100 text-green-700"
                                    title="Verify"
                                  >
                                    <Check size={15} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelected(row);
                                      setActionType("reject");
                                    }}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-red-700"
                                    title="Reject"
                                  >
                                    <X size={15} />
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Showing page {currentPage} of {lastPage} ({total} total requests)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchRows(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchRows(currentPage + 1)}
                  disabled={currentPage >= lastPage || loading}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={!!actionType}
        onClose={() => {
          setActionType(null);
          setSelected(null);
        }}
        onConfirm={handleConfirmAction}
        title={actionType === "verify" ? "Verify Offline Request" : "Reject Offline Request"}
        message={
          actionType === "verify"
            ? `Verify request #${selected?.id || ""}? This will activate selected package for the company.`
            : `Reject request #${selected?.id || ""}?`
        }
        confirmText={actionType === "verify" ? "Verify" : "Reject"}
        variant={actionType === "verify" ? "warning" : "danger"}
      />
    </div>
  );
};

export default SuperAdminOfflineRequests;

